import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { get, intersection } from '../../lib/nodash';
import { Box, Text } from 'grommet';

import SmartLink from '../SmartLink';
import {
  findMetafieldValue,
  getProductPath,
  hasDarkTag,
} from '../../lib/product';

const BreadcrumbText = ({ children, dark, ...rest }) => (
  <Text
    size="xsmall"
    color={dark ? 'white' : 'brand'}
    style={{ letterSpacing: '1px' }}
    truncate={true}
    {...rest}
  >
    {children}
  </Text>
);
BreadcrumbText.propTypes = {
  children: PropTypes.node,
  dark: PropTypes.bool,
};

const ProductBreadcrumb = ({ product, collection, base }) => {
  const isDark = hasDarkTag(get('tags', product));
  const productPath = getProductPath(product);
  const collectionHandle = get('handle', collection);
  const showBase = base && get('slug', base) !== get('handle', collection);
  const collectionUrl = getCollectionUrl(collection);
  const isCollectionPaintCollection = collectionUrl?.includes(
    '/collections/paint/'
  );
  const isCollectionPaintColorCollection =
    isCollectionPaintCollection &&
    intersection(
      (product?.tags ?? [])
        .filter((tag) => tag.toLowerCase().includes('color:'))
        .map((tag) => `${tag.replace('color:', '').trim()}-paint`),
      [collectionHandle]
    ).length > 0;

  const collectionUrlFinal = isCollectionPaintColorCollection
    ? `/collections/paint/?filter=color%3A${collectionHandle.replace(
        '-paint',
        ''
      )}`
    : collectionUrl;

  return (
    <Box direction="row" gap="small" background={{ dark: isDark }}>
      {showBase && (
        <BreadcrumbText>
          <SmartLink plain to={`/${base.slug}`}>
            {get('title', base)}
          </SmartLink>
        </BreadcrumbText>
      )}
      {showBase && <BreadcrumbText dark={isDark}>/</BreadcrumbText>}
      {collectionHandle && (
        <BreadcrumbText>
          <SmartLink plain to={collectionUrlFinal}>
            {get('title', collection)}
          </SmartLink>
        </BreadcrumbText>
      )}
      {collectionHandle && <BreadcrumbText dark={isDark}>/</BreadcrumbText>}
      <BreadcrumbText weight={500}>
        <SmartLink plain to={productPath} weight={500}>
          {product.title}
        </SmartLink>
      </BreadcrumbText>
    </Box>
  );
};

function getCollectionUrl(collection) {
  const collectionHandle = get('handle', collection);

  if (!collectionHandle) return undefined;

  const hasPaintCollection =
    findMetafieldValue('type', collection?.metafields ?? [], 'collection') ===
    'Paint Collection';

  return hasPaintCollection
    ? `/collections/paint/${collectionHandle.replace('collections/', '')}`
    : `/collections/${collectionHandle.replace('collections/', '')}`;
}

ProductBreadcrumb.propTypes = {
  product: PropTypes.object.isRequired,
  collection: PropTypes.object,
  base: PropTypes.object,
};

export default memo(ProductBreadcrumb);
